import {
  AlertCircle,
  HelpCircle,
  Server,
  Shield,
  TicketIcon,
  DollarSign,
  Bug,
  Cpu,
  Globe,
  Network,
  Key,
  Lock,
  Ban,
  CreditCard,
  Timer,
  MessageCircle,
  UserX,
  Clipboard,
} from "lucide-react";

import { TICKET_ENUMS } from "./mongo";

export const TICKET_TYPE_ICONS = {
  question_support: HelpCircle,
  staff_order_review: Clipboard,
  sales_orders: DollarSign,
  incidents_problems: Bug,
  compute_provisioning: Cpu,
  network_provisioning: Network,
  transit_request: Globe,
  peering_request: Network,
  abuse_report: Shield,
  noc: Server,
  ix: Globe,
  route_leak: AlertCircle,
  feedback: MessageCircle,
  ip_reassignment: Key,
  routed_tunnel: Lock,
  order_cancellation: Ban,
  unauthorized_announcement: UserX,
  invoice_renewal: CreditCard,
  failed_payment: Timer,
  tos_warning: AlertCircle,
  tos_violation: Shield,
  spam: Ban,
  service_suspended: AlertCircle,
  service_terminated: AlertCircle,
  order_change: TicketIcon,
  legal_request: Shield,
} as const;

export const TICKET_TYPE_DESCRIPTIONS = {
  question_support: "Get help with general questions and technical support",
  staff_order_review:
    "The ticket type that is used to review pending orders prior to provisioning",
  sales_orders: "Billing inquiries, payment issues, and order management",
  incidents_problems: "Report service outages and technical incidents",
  compute_provisioning: "Request new virtual machines and compute resources",
  network_provisioning: "Network configuration and connectivity setup",
  transit_request: "IP transit and BGP session requests",
  peering_request: "Peering arrangements and interconnection requests",
  abuse_report: "Report network abuse, spam, or malicious activity",
  noc: "Network Operations Center requests and monitoring",
  ix: "Internet Exchange port requests and configuration",
  route_leak: "Report route leaks and BGP routing issues",
  feedback: "Share feedback and suggestions for service improvement",
  ip_reassignment: "Request IP address reassignment or transfer",
  routed_tunnel: "Tunneling and VPN connection requests",
  order_cancellation: "Cancel existing orders or services",
  unauthorized_announcement: "Report unauthorized IP announcements",
  invoice_renewal: "Invoice and billing cycle management",
  failed_payment: "Payment processing and overdue account issues",
  tos_warning: "Terms of Service violation warning",
  tos_violation: "Terms of Service violation notice",
  spam: "Message marked as spam",
  service_suspended: "Notification of service suspension",
  service_terminated: "Notification of service termination",
  order_change:
    "A ticket logging the change of a product in an order, used for tracking and auditing purposes",
  legal_request: "Requests related to legal matters and compliance",
} as const;

export const SEVERITY_COLORS = {
  low: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  medium: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  high: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  urgent: "bg-red-500/15 text-red-400 border-red-500/30",
};

export const SEVERITY_DESCRIPTIONS = {
  low: "Low priority - General questions and minor issues",
  medium: "Medium priority - Standard support requests",
  high: "High priority - Business impacting issues",
  urgent: "Urgent - Service outage or critical issues",
} as const;

export const STATUS_COLORS = {
  open: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  pending: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  waitlisted: "bg-purple-500/15 text-purple-400 border-purple-500/30",
  waiting_for_provisioning: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  waiting_for_reply: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  on_hold: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  closed: "bg-gray-500/15 text-gray-400 border-gray-500/30",
  resolved: "bg-violet-500/15 text-violet-400 border-violet-500/30",
};

export const STATUS_DESCRIPTIONS = {
  open: "Ticket is open and awaiting initial response",
  pending: "Ticket is pending customer response",
  on_hold: "Ticket is temporarily on hold",
  waiting_for_provisioning: "Waiting for resource provisioning",
  waiting_for_reply: "Waiting for customer or staff reply",
  waitlisted: "Request is waitlisted pending availability",
  resolved: "Issue has been resolved",
  closed: "Ticket is closed and completed",
} as const;

export const TICKET_TYPE_LABELS = {
  question_support: "Question/Support",
  staff_order_review: "Order Review",
  sales_orders: "Sales/Billing",
  incidents_problems: "Incidents/Problems",
  compute_provisioning: "Compute Provisioning",
  network_provisioning: "Network Provisioning",
  transit_request: "Transit Request",
  peering_request: "Peering Request",
  abuse_report: "Abuse Report",
  noc: "NOC Request",
  ix: "IX Port Request",
  route_leak: "Route Leak Report",
  feedback: "Feedback",
  ip_reassignment: "IP Reassignment",
  routed_tunnel: "Routed Tunnel",
  order_cancellation: "Service Cancellation",
  unauthorized_announcement: "Unauthorized Announcement",
  invoice_renewal: "Successful Payment",
  failed_payment: "Failed Payment",
  tos_warning: "ToS Warning",
  tos_violation: "ToS Violation",
  spam: "Spam",
  service_suspended: "Service Suspended",
  service_terminated: "Service Terminated",
  order_change: "Order Change",
  legal_request: "Legal Request",
};

export const STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "open", label: "Open" },
  { value: "waitlisted", label: "Waitlisted" },
  { value: "waiting_for_provisioning", label: "Waiting for Provisioning" },
  { value: "waiting_for_reply", label: "Waiting for Reply" },
  { value: "on_hold", label: "On Hold" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

export const TYPE_OPTIONS = [
  { value: "all", label: "All Types" },
  ...Object.entries(TICKET_TYPE_LABELS).map(([value, label]) => ({
    value,
    label,
  })),
];

export const SORT_OPTIONS = [
  { value: "createdAt-desc", label: "Newest First" },
  { value: "createdAt-asc", label: "Oldest First" },
  { value: "severity-desc", label: "Highest Severity" },
  { value: "severity-asc", label: "Lowest Severity" },
];

export const ADMIN_STATUS_OPTIONS = [
  { value: "unresolved", label: "All Unresolved" },
  { value: "all", label: "All Statuses" },
  { value: "open", label: "Open" },
  { value: "pending", label: "Pending" },
  { value: "waitlisted", label: "Waitlisted" },
  { value: "waiting_for_provisioning", label: "Waiting for Provisioning" },
  { value: "waiting_for_reply", label: "Waiting for Reply" },
  { value: "on_hold", label: "On Hold" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

export const ADMIN_SORT_OPTIONS = [
  { value: "createdAt-desc", label: "Newest First" },
  { value: "createdAt-asc", label: "Oldest First" },
  { value: "updatedAt-desc", label: "Last Updated (Newest)" },
  { value: "updatedAt-asc", label: "Last Updated (Oldest)" },
  { value: "severity-desc", label: "Highest Severity" },
  { value: "severity-asc", label: "Lowest Severity" },
];

export const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

export const formatStatusDisplay = (status: string) => {
  return status
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char: string) => char.toUpperCase());
};

export const isValidTicketType = (
  type: string,
): type is (typeof TICKET_ENUMS.inquiryType)[number] => {
  return TICKET_ENUMS.inquiryType.includes(type as any);
};

export const isValidSeverity = (
  severity: string,
): severity is (typeof TICKET_ENUMS.severity)[number] => {
  return TICKET_ENUMS.severity.includes(severity as any);
};

export const isValidStatus = (
  status: string,
): status is (typeof TICKET_ENUMS.status)[number] => {
  return TICKET_ENUMS.status.includes(status as any);
};

export const getSeverityWeight = (severity: string): number => {
  const weights = { low: 1, medium: 2, high: 3, urgent: 4 };
  return weights[severity as keyof typeof weights] || 0;
};

export const getStatusWeight = (status: string): number => {
  const weights = {
    open: 1,
    pending: 2,
    waiting_for_reply: 3,
    waiting_for_provisioning: 4,
    on_hold: 5,
    waitlisted: 6,
    resolved: 7,
    closed: 8,
  };
  return weights[status as keyof typeof weights] || 0;
};

export const SCHEMA_DROPDOWN_OPTIONS: Record<string, string[]> = {
  inquiryType: TICKET_ENUMS.inquiryType,
  severity: TICKET_ENUMS.severity,
  status: TICKET_ENUMS.status,
  bgpAfi: TICKET_ENUMS.bgpAfi,
  bgpMethod: TICKET_ENUMS.bgpMethod,
};

export const TIMEFRAME_OPTIONS = [
  { value: "24_hours", label: "Last 24 Hours" },
  { value: "48_hours", label: "Last 48 Hours" },
  { value: "72_hours", label: "Last 72 Hours" },
  { value: "1_week", label: "Last Week" },
  { value: "over_a_week", label: "Over a Week" },
];

export const getDropdownOptions = (fieldName: string): string[] | null => {
  return SCHEMA_DROPDOWN_OPTIONS[fieldName] || null;
};

export const hasDropdownOptions = (fieldName: string): boolean => {
  return fieldName in SCHEMA_DROPDOWN_OPTIONS;
};

export const getFieldValue = (
  fieldName: string,
  fieldUpdates: Record<string, any>,
  currentTicket: Record<string, any>,
): string => {
  return (fieldUpdates[fieldName] ?? currentTicket[fieldName] ?? "").toString();
};

export const getTicketsByStatus = (tickets: any[], status: string) => {
  if (status === "all") return tickets;
  return tickets.filter((t) => t.status === status);
};

export const getTicketsByPriority = (
  tickets: any[],
  minPriority: string = "medium",
) => {
  const priorityWeight = getSeverityWeight(minPriority);
  return tickets.filter((t) => getSeverityWeight(t.severity) >= priorityWeight);
};

export const getRecentTickets = (tickets: any[], days: number = 7) => {
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - days);
  return tickets.filter((t) => new Date(t.createdAt) >= cutoffDate);
};

export const getTicketStats = (tickets: any[]) => {
  const stats = {
    total: tickets.length,
    byStatus: {} as Record<string, number>,
    bySeverity: {} as Record<string, number>,
    byType: {} as Record<string, number>,
    averageResponseTime: 0,
    resolved: 0,
    unresolved: 0,
    open: 0,
    pending: 0,
    on_hold: 0,
    waiting_for_provisioning: 0,
    waiting_for_reply: 0,
    waitlisted: 0,
    closed: 0,
  };

  tickets.forEach((ticket) => {
    stats.byStatus[ticket.status] = (stats.byStatus[ticket.status] || 0) + 1;

    stats.bySeverity[ticket.severity] =
      (stats.bySeverity[ticket.severity] || 0) + 1;

    const typeLabel =
      TICKET_TYPE_LABELS[
        ticket.inquiryType as keyof typeof TICKET_TYPE_LABELS
      ] || ticket.inquiryType;
    stats.byType[typeLabel] = (stats.byType[typeLabel] || 0) + 1;

    // Count individual statuses
    switch (ticket.status) {
      case "open":
        stats.open++;
        stats.unresolved++;
        break;
      case "pending":
        stats.pending++;
        stats.unresolved++;
        break;
      case "on_hold":
        stats.on_hold++;
        stats.unresolved++;
        break;
      case "waiting_for_provisioning":
        stats.waiting_for_provisioning++;
        stats.unresolved++;
        break;
      case "waiting_for_reply":
        stats.waiting_for_reply++;
        stats.unresolved++;
        break;
      case "waitlisted":
        stats.waitlisted++;
        stats.unresolved++;
        break;
      case "resolved":
        stats.resolved++;
        break;
      case "closed":
        stats.closed++;
        break;
      default:
        stats.unresolved++;
        break;
    }
  });

  return stats;
};

export const getUrgentNotifications = (tickets: any[]) => {
  const notifications = [];

  const urgentTickets = tickets.filter(
    (t) =>
      getSeverityWeight(t.severity) >= 3 &&
      ![
        "closed",
        "resolved",
        "waitlisted",
        "waiting_for_provisioning",
        "on_hold",
      ].includes(t.status),
  );

  if (urgentTickets.length > 0) {
    notifications.push({
      type: "urgent_tickets",
      count: urgentTickets.length,
      message: `You have ${urgentTickets.length} urgent ticket${urgentTickets.length > 1 ? "s" : ""}`,
      tickets: urgentTickets.slice(0, 3),
      severity: "urgent",
    });
  }

  const threeDaysAgo = new Date();
  threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);

  const staleTickets = tickets.filter(
    (t) =>
      new Date(t.updatedAt) < threeDaysAgo &&
      !["closed", "resolved"].includes(t.status),
  );

  if (staleTickets.length > 0) {
    notifications.push({
      type: "stale_tickets",
      count: staleTickets.length,
      message: `${staleTickets.length} ticket${staleTickets.length > 1 ? "s have" : " has"} no recent activity`,
      tickets: staleTickets.slice(0, 3),
      severity: "medium",
    });
  }

  return notifications;
};

export const getRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMinutes = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60),
  );

  if (diffInMinutes < 1) return "Just now";
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}d ago`;

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) return `${diffInWeeks}w ago`;

  const diffInMonths = Math.floor(diffInDays / 30);
  return `${diffInMonths}mo ago`;
};

export const isOverdue = (ticket: any, hours: number = 24): boolean => {
  if (["closed", "resolved"].includes(ticket.status)) {
    return false;
  }

  const lastUpdate = new Date(ticket.updatedAt);
  const now = new Date();
  const diffInHours = (now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60);

  const overdueThreshold = {
    urgent: 4,
    high: 12,
    medium: 24,
    low: 48,
  };

  return (
    diffInHours >
    (overdueThreshold[ticket.severity as keyof typeof overdueThreshold] ||
      hours)
  );
};

export const getTicketThemeColors = (ticket: any) => {
  const severityTheme = {
    urgent: {
      bg: "from-red-500/10 to-red-600/5",
      border: "border-red-500/30",
      text: "text-red-400",
      icon: "text-red-400",
    },
    high: {
      bg: "from-orange-500/10 to-orange-600/5",
      border: "border-orange-500/30",
      text: "text-orange-400",
      icon: "text-orange-400",
    },
    medium: {
      bg: "from-amber-500/10 to-amber-600/5",
      border: "border-amber-500/30",
      text: "text-amber-400",
      icon: "text-amber-400",
    },
    low: {
      bg: "from-emerald-500/10 to-emerald-600/5",
      border: "border-emerald-500/30",
      text: "text-emerald-400",
      icon: "text-emerald-400",
    },
  };

  return (
    severityTheme[ticket.severity as keyof typeof severityTheme] ||
    severityTheme.medium
  );
};

export const searchTickets = (tickets: any[], query: string) => {
  if (!query.trim()) return tickets;

  const searchLower = query.toLowerCase();
  return tickets.filter(
    (ticket) =>
      ticket.name.toLowerCase().includes(searchLower) ||
      ticket.email.toLowerCase().includes(searchLower) ||
      ticket.description.toLowerCase().includes(searchLower) ||
      ticket.ticketId.toString().includes(searchLower) ||
      (ticket.orderId && ticket.orderId.toLowerCase().includes(searchLower)) ||
      TICKET_TYPE_LABELS[ticket.inquiryType as keyof typeof TICKET_TYPE_LABELS]
        ?.toLowerCase()
        .includes(searchLower),
  );
};

export const canBulkUpdate = (tickets: any[], operation: string): boolean => {
  if (!tickets.length) return false;

  switch (operation) {
    case "close":
      return tickets.some((t) => !["closed", "resolved"].includes(t.status));
    case "priority":
      return tickets.every((t) => !["closed", "resolved"].includes(t.status));
    case "assign":
      return tickets.every((t) => !["closed"].includes(t.status));
    default:
      return false;
  }
};

export function formatInquiryType(type: string): string {
  return type
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
